import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DataType, PhotoProviderBase, OverlayRenderProps, ReachType } from './types';
import { defaultEasing, defaultSpeed, defaultOpacity, horizontalOffset, maxMoveOffset } from './variables';
import isTouchDevice from './utils/isTouchDevice';
import { limitNumber } from './utils/limitTarget';
import useIsomorphicLayoutEffect from './hooks/useIsomorphicLayoutEffect';
import useAdjacentImages from './hooks/useAdjacentImages';
import useSetState from './hooks/useSetState';
import useEventListener from './hooks/useEventListener';
import useAnimationVisible from './hooks/useAnimationVisible';
import useMethods from './hooks/useMethods';
import SlidePortal from './components/SlidePortal';
import ArrowLeft from './components/ArrowLeft';
import ArrowRight from './components/ArrowRight';
import PreventScroll from './components/PreventScroll';
import CustomToolbars from './components/CustomToolbars';
import PhotoBox from './PhotoBox';
import './PhotoSlider.less';
import { getInnerWidth, isMobile, setDivRef, setFullScreen } from './utils/getInnerWidth';
import ProtectionManager from './components/ProtectionManager';

export interface IPhotoSliderProps extends PhotoProviderBase {
  // 图片列表
  images: DataType[];
  // 图片当前索引
  index?: number;
  fullScreen: boolean;
  // 索引改变回调
  onIndexChange?: (index: number) => void;
  onFullScreen: (status: boolean) => void;
  // 可见
  visible: boolean;
  // 关闭回调
  onClose: (evt?: React.MouseEvent | React.TouchEvent) => void;
  // 关闭动画结束后回调
  afterClose?: () => void;
  // Renderizador personalizado de capa de protección
  protectionLayerEnable?: boolean;
  protectionLayerRender?: (props: {
    isVisible: boolean;
    onRestore: () => void;
    reason: 'focus' | 'command' | 'printscreen';
  }) => React.ReactNode;
}

type PhotoSliderState = {
  // 偏移量
  x: number;
  // 图片处于触摸的状态
  touched: boolean;
  // 是否暂停 transition
  pause: boolean;
  // Reach 开始时 x 坐标
  lastCX: number | undefined;
  // Reach 开始时 y 坐标
  lastCY: number | undefined;
  // 背景透明度
  bg: number | null | undefined;
  // 上次关闭的背景透明度
  lastBg: number | null | undefined;
  // 是否显示 overlay
  overlay: boolean;
  // 是否为最小状态，可下拉关闭
  minimal: boolean;
  // 缩放
  scale: number;
  // 旋转
  rotate: number;
  // 缩放回调
  onScale?: (scale: number) => void;
  // 旋转回调
  onRotate?: (rotate: number) => void;
};

const initialState: PhotoSliderState = {
  x: 0,
  touched: false,
  pause: false,
  lastCX: undefined,
  lastCY: undefined,
  bg: undefined,
  lastBg: undefined,
  overlay: true,
  minimal: true,
  scale: 1,
  rotate: 0,
};

// eslint-disable-next-line max-lines-per-function
export default function PhotoSlider(props: IPhotoSliderProps) {
  const {
    loop = 3,
    speed: speedFn,
    easing: easingFn,
    photoClosable,
    maskClosable = true,
    maskOpacity = defaultOpacity,
    pullClosable = true,
    pullAction,
    bannerVisible = true,
    overlayRender,
    toolbarRender,
    showBackdropImage,
    disableScroll = true,
    toolbarLeftRender,
    toolbarRightRender,
    toolbarBottomRender,
    sidebarWidth,
    sidebarLeftRender,
    sidebarRightRender,
    className,
    maskClassName,
    photoClassName,
    photoWrapClassName,
    loadingElement,
    brokenElement,
    images,
    index: controlledIndex = 0,
    onIndexChange: controlledIndexChange,
    visible,
    onClose,
    afterClose,
    portalContainer,
    fullScreen,
    onFullScreen,
    protectionLayerEnable,
    protectionLayerRender,
  } = props;

  const [state, updateState] = useSetState(initialState);
  const [innerIndex, updateInnerIndex] = useState(0);

  const containerPreviewRef = useRef<any>();

  useEffect(() => {
    if (containerPreviewRef.current) {
      setDivRef(containerPreviewRef.current); // Capturar el div aquí
    }

    return () => {
      setDivRef(null); // Limpia la referencia al desmontar
    };
  }, [visible]);

  const {
    x,
    touched,
    pause,

    lastCX,
    lastCY,

    bg = maskOpacity,
    lastBg,
    overlay,
    minimal,

    scale,
    rotate,
    onScale,
    onRotate,
  } = state;

  // 受控 index
  const isControlled = props.hasOwnProperty('index');
  const index = isControlled ? controlledIndex : innerIndex;
  const onIndexChange = isControlled ? controlledIndexChange : updateInnerIndex;
  // 内部虚拟 index
  const virtualIndexRef = useRef(index);

  // 当前图片
  const imageLength = images.length;
  const currentImage: DataType | undefined = images[index];

  // 是否开启
  // noinspection SuspiciousTypeOfGuard
  const enableLoop = typeof loop === 'boolean' ? loop : imageLength > loop;

  // 显示动画处理
  const [realVisible, activeAnimation, onAnimationEnd] = useAnimationVisible(visible, afterClose);

  useIsomorphicLayoutEffect(() => {
    // 显示弹出层，修正正确的指向
    if (realVisible) {
      updateState({
        pause: true,
        x: index * -(getInnerWidth() + horizontalOffset),
      });
      virtualIndexRef.current = index;
      return;
    }
    // 关闭后清空状态
    updateState(initialState);
  }, [realVisible]);

  const { close, changeIndex } = useMethods({
    close(evt?: React.MouseEvent | React.TouchEvent) {
      if (onRotate) {
        onRotate(0);
      }
      updateState({
        overlay: true,
        // 记录当前关闭时的透明度
        lastBg: bg,
      });
      onClose(evt);
    },
    changeIndex(nextIndex: number, isPause: boolean = false) {
      // 当前索引
      const currentIndex = enableLoop ? virtualIndexRef.current + (nextIndex - index) : nextIndex;
      const max = imageLength - 1;
      // 虚拟 index
      // 非循环模式，限制区间
      const limitIndex = limitNumber(currentIndex, 0, max);
      const nextVirtualIndex = enableLoop ? currentIndex : limitIndex;
      // 单个屏幕宽度
      const singlePageWidth = getInnerWidth() + horizontalOffset;

      updateState({
        touched: false,
        lastCX: undefined,
        lastCY: undefined,
        x: -singlePageWidth * nextVirtualIndex,
        pause: isPause,
      });

      virtualIndexRef.current = nextVirtualIndex;
      // 更新真实的 index
      const realLoopIndex = nextIndex < 0 ? max : nextIndex > max ? 0 : nextIndex;
      if (onIndexChange) {
        onIndexChange(enableLoop ? realLoopIndex : limitIndex);
      }
    },
  });

  const toggleFullScreenState = useCallback(() => {
    onFullScreen(!fullScreen);
  }, [fullScreen, onFullScreen]);

  const handleFullScreenChange = useMemo(
    () => () => {
      const isFullScreen = document.fullscreenElement;
      // setFullScreenIcon(isFullScreen ? 'min' : 'max');
      if (!isFullScreen && fullScreen) {
        toggleFullScreenState();
      }
    },
    [fullScreen, toggleFullScreenState],
  );

  const isFullScreenAvailable = () => {
    return (
      (document.documentElement as any).requestFullscreen ||
      (document.documentElement as any).mozRequestFullScreen || // Firefox
      (document.documentElement as any).webkitRequestFullscreen || // Chrome, Safari, Opera
      (document.documentElement as any).msRequestFullscreen // IE/Edge
    );
  };

  const goFullScreen = (videoElement: any) => {
    try {
      if (videoElement) {
        if (videoElement.requestFullscreen) {
          videoElement.requestFullscreen();
        } else if (videoElement.mozRequestFullScreen) {
          // Firefox
          videoElement.mozRequestFullScreen();
        } else if (videoElement.webkitRequestFullscreen) {
          // Chrome, Safari, Opera
          videoElement.webkitRequestFullscreen();
        } else if (videoElement.msRequestFullscreen) {
          // IE/Edge
          videoElement.msRequestFullscreen();
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('Error al intentar activar el modo de pantalla completa', error);
    }
  };

  const toggleFullScreen = useMemo(
    () => () => {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else if (fullScreen) {
        const element = containerPreviewRef.current;
        if (!element) {
          return;
        }

        goFullScreen(element);
      }
    },
    [containerPreviewRef, fullScreen],
  );

  useEffect(() => {
    if (!containerPreviewRef.current) {
      return;
    }

    toggleFullScreen();

    setFullScreen(fullScreen);

    document.addEventListener('fullscreenchange', handleFullScreenChange);

    // eslint-disable-next-line consistent-return
    return () => {
      document.removeEventListener('fullscreenchange', handleFullScreenChange);
    };
  }, [containerPreviewRef, fullScreen, handleFullScreenChange, toggleFullScreen]);

  useEventListener('keydown', (evt: KeyboardEvent) => {
    if (visible) {
      switch (evt.key) {
        case 'ArrowLeft':
          changeIndex(index - 1, false);
          break;
        case 'ArrowRight':
          changeIndex(index + 1, false);
          break;
        case 'Escape':
          close();
          break;
        default:
      }
    }
  });

  function handlePhotoTap(closeable: boolean | undefined) {
    return closeable ? close() : updateState({ overlay: !overlay });
  }

  function handleResize() {
    updateState({
      x: -(getInnerWidth() + horizontalOffset) * index,
      lastCX: undefined,
      lastCY: undefined,
      pause: true,
    });
    virtualIndexRef.current = index;
  }

  function handleReachVerticalMove(clientY: number, nextScale?: number) {
    if (lastCY === undefined) {
      updateState({
        touched: true,
        lastCY: clientY,
        bg,
        minimal: true,
      });
      return;
    }
    const opacity =
      maskOpacity === null ? null : limitNumber(maskOpacity, 0.01, maskOpacity - Math.abs(clientY - lastCY) / 100 / 4);

    updateState({
      touched: true,
      lastCY,
      bg: nextScale === 1 ? opacity : maskOpacity,
      minimal: nextScale === 1,
    });
  }

  function handleReachHorizontalMove(clientX: number) {
    if (lastCX === undefined) {
      updateState({
        touched: true,
        lastCX: clientX,
        x,
        pause: false,
      });
      return;
    }
    const originOffsetClientX = clientX - lastCX;
    let offsetClientX = originOffsetClientX;

    // 第一张和最后一张超出距离减半
    if (
      !enableLoop &&
      ((index === 0 && originOffsetClientX > 0) || (index === imageLength - 1 && originOffsetClientX < 0))
    ) {
      offsetClientX = originOffsetClientX / 2;
    }

    updateState({
      touched: true,
      lastCX,
      x: -(getInnerWidth() + horizontalOffset) * virtualIndexRef.current + offsetClientX,
      pause: false,
    });
  }

  function handleReachMove(reachPosition: ReachType, clientX: number, clientY: number, nextScale?: number) {
    if (reachPosition === 'x') {
      handleReachHorizontalMove(clientX);
    } else if (reachPosition === 'y') {
      handleReachVerticalMove(clientY, nextScale);
    }
  }

  function handleReachUp(clientX: number, clientY: number) {
    const offsetClientX = clientX - (lastCX ?? clientX);
    const offsetClientY = clientY - (lastCY ?? clientY);
    let willClose = false;
    // 下一张
    if (offsetClientX < -maxMoveOffset) {
      changeIndex(index + 1);
      return;
    }
    // 上一张
    if (offsetClientX > maxMoveOffset) {
      changeIndex(index - 1);
      return;
    }
    const singlePageWidth = getInnerWidth() + horizontalOffset;
    // 当前偏移
    const currentTranslateX = -singlePageWidth * virtualIndexRef.current;

    if (Math.abs(offsetClientY) > 100 && minimal && pullClosable) {
      willClose = true;
      close();
    } else if (Math.abs(offsetClientY) > 100 && minimal && typeof pullAction === 'function') {
      pullAction({ direction: offsetClientY < 0 ? 'top' : 'bottom', offsetY: offsetClientY });
    }
    updateState({
      touched: false,
      x: currentTranslateX,
      lastCX: undefined,
      lastCY: undefined,
      bg: maskOpacity,
      overlay: willClose ? true : overlay,
    });
  }
  // 截取相邻的图片
  const adjacentImages = useAdjacentImages(images, index, enableLoop);

  if (!realVisible) {
    return null;
  }

  const currentOverlayVisible = overlay && !activeAnimation;
  // 关闭过程中使用下拉保存的透明度
  const currentOpacity = visible ? bg : lastBg;
  // 覆盖物参数
  const overlayParams: OverlayRenderProps | undefined = onScale &&
    onRotate && {
      images,
      index,
      visible,
      onClose: close,
      onIndexChange: changeIndex,
      overlayVisible: currentOverlayVisible,
      overlay: currentImage && currentImage.overlay,
      scale,
      rotate,
      onScale,
      onRotate,
      fullScreen,
      fullScreenAvailable: isFullScreenAvailable(),
      toggleFullScreen: () => onFullScreen(!fullScreen),
    };
  // 动画时间
  const currentSpeed = speedFn ? speedFn(activeAnimation) : defaultSpeed;
  const currentEasing = easingFn ? easingFn(activeAnimation) : defaultEasing;
  const slideSpeed = speedFn ? speedFn(3) : defaultSpeed + 200;
  const slideEasing = easingFn ? easingFn(3) : defaultEasing;
  const sidebarResolvedWidth = typeof sidebarWidth === 'number' ? `${sidebarWidth}px` : sidebarWidth;
  const isMobileSize = isMobile();


  function handleContextMenu(e: React.MouseEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
  }

  return (
    <SlidePortal
      className={`PhotoView-Portal${!currentOverlayVisible ? ' PhotoView-Slider__clean' : ''}${
        !visible ? ' PhotoView-Slider__willClose' : ''
      }${className ? ` ${className}` : ''}`}
      role="dialog"
      onContextMenu={handleContextMenu}
      onClick={(e) => e.stopPropagation()}
      container={portalContainer}
    >
      {visible && disableScroll && <PreventScroll />}

      <div className="PhotoView-Container">
        {sidebarLeftRender && overlayParams && !isMobileSize && (
          <div
            className={`PhotoView-Sidebar PhotoView-Sidebar__Left ${
              activeAnimation === 1
                ? ' PhotoView-Slider__fadeIn'
                : activeAnimation === 2
                  ? ' PhotoView-Slider__fadeOut'
                  : ''
            }`}
            style={{
              width: sidebarResolvedWidth,
              transitionTimingFunction: currentEasing,
              transitionDuration: `${touched ? 0 : currentSpeed}ms`,
              animationDuration: `${currentSpeed}ms`,
            }}
          >
            {sidebarLeftRender(overlayParams)}
          </div>
        )}

        <div ref={containerPreviewRef} className="PhotoView-Viewer">
          <div
            className={`PhotoView-Slider__Backdrop${maskClassName ? ` ${maskClassName}` : ''}${
              activeAnimation === 1
                ? ' PhotoView-Slider__fadeIn'
                : activeAnimation === 2
                  ? ' PhotoView-Slider__fadeOut'
                  : ''
            } ${showBackdropImage ? 'with-backdrop-image' : ''}`}
            style={{
              background: showBackdropImage
                ? `url(${currentImage.src})`
                : currentOpacity
                  ? `rgba(0, 0, 0, ${currentOpacity})`
                  : undefined,
              transitionTimingFunction: currentEasing,
              transitionDuration: `${touched ? 0 : currentSpeed}ms`,
              animationDuration: `${currentSpeed}ms`,
            }}
            onAnimationEnd={onAnimationEnd}
          />

          <CustomToolbars
            bannerVisible={bannerVisible}
            index={index}
            imageLength={imageLength}
            overlayParams={overlayParams}
            toolbarRender={toolbarRender}
            toolbarLeftRender={toolbarLeftRender}
            toolbarRightRender={toolbarRightRender}
            toolbarBottomRender={toolbarBottomRender}
          />

          {adjacentImages.map((item: DataType, currentIndex) => {
            // 截取之前的索引位置
            const nextIndex =
              !enableLoop && index === 0 ? index + currentIndex : virtualIndexRef.current - 1 + currentIndex;

            return (
              <PhotoBox
                key={enableLoop ? `${item.key}/${item.src}/${nextIndex}` : item.key}
                item={item}
                speed={currentSpeed}
                easing={currentEasing}
                visible={visible}
                onReachMove={handleReachMove}
                onReachUp={handleReachUp}
                onPhotoTap={() => handlePhotoTap(photoClosable)}
                onMaskTap={() => handlePhotoTap(maskClosable)}
                wrapClassName={photoWrapClassName}
                className={photoClassName}
                style={{
                  left: `${(getInnerWidth() + horizontalOffset) * nextIndex}px`,
                  transform: `translate3d(${x}px, 0px, 0)`,
                  transition: touched || pause ? undefined : `transform ${slideSpeed}ms ${slideEasing}`,
                }}
                loadingElement={loadingElement}
                brokenElement={brokenElement}
                onPhotoResize={handleResize}
                isActive={virtualIndexRef.current === nextIndex}
                expose={updateState}
              />
            );
          })}
          {!isTouchDevice && bannerVisible && (
            <>
              {(enableLoop || index !== 0) && (
                <div className="PhotoView-Slider__ArrowLeft" onClick={() => changeIndex(index - 1, true)}>
                  <ArrowLeft />
                </div>
              )}
              {(enableLoop || index + 1 < imageLength) && (
                <div className="PhotoView-Slider__ArrowRight" onClick={() => changeIndex(index + 1, true)}>
                  <ArrowRight />
                </div>
              )}
            </>
          )}
          {overlayRender && overlayParams && (
            <div className="PhotoView-Slider__Overlay">{overlayRender(overlayParams)}</div>
          )}
        </div>

        {sidebarRightRender && overlayParams && !isMobileSize && (
          <div
            className={`PhotoView-Sidebar PhotoView-Sidebar__Right ${
              activeAnimation === 1
                ? ' PhotoView-Slider__fadeIn'
                : activeAnimation === 2
                  ? ' PhotoView-Slider__fadeOut'
                  : ''
            }`}
            style={{
              width: sidebarResolvedWidth,
              transitionTimingFunction: currentEasing,
              transitionDuration: `${touched ? 0 : currentSpeed}ms`,
              animationDuration: `${currentSpeed}ms`,
            }}
          >
            {sidebarRightRender(overlayParams)}
          </div>
        )}

        {toolbarBottomRender && overlayParams && (
          <div
            className="PhotoView-Slider__Toolbar PhotoView-Slider__Toolbar--Bottom"
            style={{
              width: `calc(100% - ${!isMobileSize ? sidebarResolvedWidth : 0})`,
            }}
          >
            {toolbarBottomRender(overlayParams)}
          </div>
        )}

        <ProtectionManager enabled={protectionLayerEnable || false} protectionLayerRender={protectionLayerRender} />
      </div>
    </SlidePortal>
  );
}
